import React from 'react';
import { connect } from 'react-redux';
import { map, addIndex } from 'ramda';
import ScrollArea from 'react-scrollbar';
import moment from 'moment';
import { withRouter } from 'react-router-dom';

import { Grid, GridHeader, GridBody, GridRow, GridCell } from '../../components/basic/grid';
import StatusBadge from '../../components/results/StatusBadge';
import ScoreChart from '../../components/results/ScoreChart';
import Button from '../../components/basic/Button';
import AdminOnly from './AdminOnly';

import { getCurrentResult } from '../../selectors/ResultsSelector';
import * as resultActions from '../../actions/entities/ResultActions';
import { impersonateUser } from '../../actions/entities/UserActions';
import { describeEvent, formatDuration } from '../../utils/Utils';

import './ResultContainer.css';

class ResultContainer extends React.Component {
  componentDidMount() {
    const { fetchResult, match: { params: { projectId, id, gameId } } } = this.props;
    fetchResult(projectId, id, gameId);
  }

  getActiveTab = () => {
    const { location } = this.props;
    const query = new URLSearchParams(location.search);
    return query.get('tab') || 'events';
  };

  setActiveTab = (tabName) => {
    const { history, location } = this.props;
    const query = new URLSearchParams(location.search);
    query.set('tab', tabName);
    history.push({
      pathname: location.pathname,
      search: query.toString()
    });
  };

  renderTabContent = () => {
    const activeTab = this.getActiveTab();
    const { currentResult } = this.props;
    const { events, conversations } = currentResult;
    const firstEventTimestamp = (events[events.length - 1] || {}).timestamp;

    switch (activeTab) {
      case 'events':
        return (
          <Grid>
            <GridHeader>
              <GridRow className="row">
                <GridCell className="col-sm-3">
                  <span>Timestamp</span>
                </GridCell>
                <GridCell className="col-sm-3">
                  <span>Type</span>
                </GridCell>
                <GridCell className="col-sm-4">
                  <span>Data</span>
                </GridCell>
                <GridCell className="col-sm-2">
                  <span>Task</span>
                </GridCell>
              </GridRow>
            </GridHeader>
            <ScrollArea>
              <GridBody>
                {map(
                  e => (
                    <GridRow className="row" key={`${e.timestamp}/${e.type}`}>
                      <GridCell className="col-sm-3">
                        <span>{moment(e.timestamp).format()}</span>
                        <br />
                        <small className="font-weight-light">
                          + {formatDuration(Math.floor((e.timestamp - firstEventTimestamp) / 1000))}
                        </small>
                      </GridCell>
                      <GridCell className="col-sm-3">
                        <span>{describeEvent(e.type)}</span>
                      </GridCell>
                      <GridCell className="col-sm-4">
                        <small>
                          <pre>{JSON.stringify(e.data)}</pre>
                        </small>
                      </GridCell>
                      <GridCell className="col-sm-2">
                        <span>{e.taskKey}</span>
                      </GridCell>
                    </GridRow>
                  ),
                  events
                )}
                {events.length === 0 && (
                  <GridRow className="row">
                    <GridCell className="col-sm-12 text-center">
                      <span>No events yet</span>
                    </GridCell>
                  </GridRow>
                )}
              </GridBody>
            </ScrollArea>
          </Grid>
        );
      case 'conversation':
        return (
          <div className="flex flex1">
            <ScrollArea>
              {map(c => (
                <div key={c.id} className="Conversation-messages-container">
                  {map(m => {
                    const messages = m.messages != null && m.messages.length > 0 ? m.messages : [{ message: '' }];
                    return (
                      <div key={m.id} className={`Conversation-messages type-${m.type}`}>
                        <div className="Conversation-messages-inner">
                          {addIndex(map)((mm, idx) => (
                            <div key={idx} className="Conversation-message">
                              {mm.message ? (
                                <div dangerouslySetInnerHTML={{ __html: mm.message }} />
                              ) : (
                                <em style={{ opacity: 0.5 }}>No answer</em>
                              )}
                              {m.type === 'SENT' && idx === messages.length - 1 && (
                                <span className="Conversation-message-timestamp">
                                  {moment(m.timestamp).format('LLLL')}
                                </span>
                              )}
                            </div>
                          ), messages)}
                        </div>
                        {m.comment && (
                          <div className="Conversation-comment">
                            {m.comment}
                          </div>
                        )}
                      </div>
                    )
                  }, c.messages)}
                </div>
              ), conversations || [])}
            </ScrollArea>
          </div>
        );
      default:
        return null;
    }
  };

  render() {
    const { currentResult, loading, impersonateUser } = this.props;
    if (!currentResult || !currentResult.participant || loading) {
      return null;
    }
    const { participant, status } = currentResult;
    const activeTab = this.getActiveTab();

    return (
      <div className="flex flex1 Result-container">
        <div className="Result-container-header">
          <div style={{ float: 'left', display: 'inline-block', fontSize: 0, marginRight: '8px' }}>
            <ScoreChart result={currentResult.result} size={60} />
          </div>
          {participant.lastName || participant.firstName ? (
            <span className="Card-title">
              {participant.lastName} {participant.firstName}
              <StatusBadge status={status} />
            </span>
          ) : (
            <em className="Card-title">
              Unnamed
              <StatusBadge status={status} />
            </em>
          )}
          <span className="Card-subtitle">{participant.email}</span>
          <AdminOnly noProd>
            <div style={{ marginTop: '16px' }}>
              <Button
                raised
                label="Go to portal"
                onClick={() => impersonateUser(participant.id, 'PORTAL')}
              />
            </div>
          </AdminOnly>
        </div>
        
        <div className="Result-tabs flex flex1 flex-column">
          <ul className="Result-tabs-nav">
            <li 
              className={activeTab === 'events' ? 'active' : ''} 
              onClick={() => this.setActiveTab('events')}
            >
              Events
            </li>
            <li 
              className={activeTab === 'conversation' ? 'active' : ''} 
              onClick={() => this.setActiveTab('conversation')}
            >
              Conversation
            </li>
          </ul>
          <div className="Result-tabs-content flex flex1">
            {this.renderTabContent()}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const { loader: { loading } } = state;
  return {
    loading,
    currentResult: getCurrentResult(state)
  };
};

export default withRouter(connect(mapStateToProps, { ...resultActions, impersonateUser })(ResultContainer));



// WEBPACK FOOTER //
// ./src/containers/private/ResultContainer.js